// The Offer page (lane B8, S12): the routed page. It reads `offer.open`, draws the frame (`OfferPageView.tsx`) and
// mounts the tab the route names: the Offer tab (S12), Compensation and Reference Check (S13).
// Hosted by MissionShell: views/b8/register.ts registers it through S12's registerView, so it draws no McRoot of its own;
// the read's refusal and loading boundary are ObjectReadGuard's, which renders them through the shell's V1 slot.

import { useEffect } from "react";
import { compOpenWorksheet, offerOpen, type OfferOpenOutput } from "@/api/tools";
import { isClientRefusal, useRead } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import { ObjectReadGuard } from "@/ui";
import { Skeleton } from "@/mc";
import { OfferTab } from "./OfferTab";
import { OfferPageView, type OfferPageTab } from "./OfferPageView";
import { CompensationTab } from "./package/CompensationTab";
import { ReferenceTab } from "./reference/ReferenceTab";
import { seam, type OfferTabProps } from "./types";
import "./offer.css";

export { OfferPageView, OfferStatusPill, tabOf, type OfferPageTab, type OfferPageViewProps } from "./OfferPageView";

export interface OfferPageProps {
  rid: string;
  aid: string;
  /** The tab the route's `?tab=` names (`tabOf`), the Offer tab by default. */
  tab: OfferPageTab;
}

export function OfferPage({ rid, aid, tab }: OfferPageProps) {
  const { register } = useViewState();
  const read = useRead(["offer", rid, aid], () => offerOpen({ requisition_id: rid, application_id: aid }));
  useEffect(
    () => register({ view: "offer", ids: { requisition: rid, application: aid }, summary: `offer ${read.data?.status ?? ""}` }),
    [register, rid, aid, read.data?.status],
  );
  if (read.isLoading) return <Skeleton lines={8} />;
  // Total Rewards work deep-links Compensation here (`?tab=compensation`): the worksheet is theirs to read, the offer
  // is not (offer.open does not admit them), so their page is framed by the worksheet and holds its tab alone
  if (!read.data && tab === "compensation" && isClientRefusal(read.error)) return <WorksheetPage rid={rid} aid={aid} />;
  // the guard is the earlier screen's (refusal → not found, failure → retry banner): drawn outside the design system
  if (!read.data) return <ObjectReadGuard query={{ data: read.data, error: read.error, refetch: read.refetch }} />;
  const offer = seam(read.data);
  const props: OfferTabProps = { requisitionId: rid, applicationId: aid, offer: read.data, reload: () => void read.refetch() };
  const panel = tab === "compensation" ? <CompensationTab {...props} /> : tab === "reference" ? <ReferenceTab {...props} /> : <OfferTab {...props} />;
  return (
    <OfferPageView requisitionId={rid} applicationId={aid} offer={offer} tab={tab}>
      {panel}
    </OfferPageView>
  );
}

/** The Compensation tab for a reader of the worksheet who does not read the offer: the frame's candidate and status
 *  come from the worksheet (the same read the tab draws, one cache entry), and the other two tabs are not offered. */
function WorksheetPage({ rid, aid }: { rid: string; aid: string }) {
  const read = useRead(["comp.open_worksheet", rid, aid] as const, () => compOpenWorksheet({ requisition_id: rid, application_id: aid }));
  if (read.isLoading) return <Skeleton lines={8} />;
  if (!read.data) return <ObjectReadGuard query={{ data: read.data, error: read.error, refetch: read.refetch }} />;
  const frame = { candidate: read.data.candidate, status: read.data.offer_status, fields: {}, decision: null };
  const props: OfferTabProps = { requisitionId: rid, applicationId: aid, offer: frame as unknown as OfferOpenOutput, reload: () => void read.refetch() };
  return (
    <OfferPageView requisitionId={rid} applicationId={aid} offer={seam(props.offer)} tab="compensation" tabs={["compensation"]}>
      <CompensationTab {...props} />
    </OfferPageView>
  );
}
